import {
  useCallback, useEffect, useState,
} from 'react';

import { resolveMemberLabel } from 'helper/flux/myStudyBlock';
import { isStudyNotFound } from 'helper/flux/myStudyErrors';
import { useControlPanelOptionsQuery } from 'services/api/Meteorology';
import { useGetMyStudyScenariosQuery } from 'services/api/myStudies';
import useStudiesErrorState from 'utils/hooks/flux/personalized/useStudiesErrorState';
import { MyStudyBlock, MyStudyScenario } from 'types/Flux/Personalized/myStudies';

/**
 * O que o painel mostra: carregando (1ª carga, reabertura depois de um erro e "Tentar de novo"),
 * erro local, estudo que não existe mais (404) ou os cenários.
 */
export type MyStudyScenariosView = 'loading' | 'error' | 'not-found' | 'ready';

export interface MyStudyScenariosState {
  view: MyStudyScenariosView;
  /** Cenários do estudo, na ordem do backend; vazio fora do `ready`. */
  scenarios: MyStudyScenario[];
  /** Refaz a consulta ("Tentar de novo"). */
  retry: () => void;
  /** Rótulo do membro do bloco pelo `painel-options` do Flux; null sem membro. */
  memberLabelOf: (block: MyStudyBlock) => string | null;
}

const NO_SCENARIOS: MyStudyScenario[] = [];

/**
 * Erros de cenários que algum painel aberto já mostrou. Um painel que monta com um deles no
 * cache continua a mesma sequência de falhas: não gera evento novo no Sentry. Fraco: o erro sai
 * daqui junto com a entrada do cache.
 */
const errorsShownInPanels = new WeakSet<object>();

const isObject = (value: unknown): value is object => typeof value === 'object' && value !== null;

/**
 * Cenários e blocos de um estudo para a linha aberta de Meus Estudos (#2002).
 *
 * - Pede `/estudos/{id}/cenarios/` só com o painel montado (quem chama só existe com a linha
 *   aberta). O cache é o da #1993: `keepUnusedDataFor` de 30 min e tag própria. Reabrir o mesmo
 *   estudo nesse prazo não refaz a requisição e já começa no `ready`.
 * - Erros pelo `useStudiesErrorState(error, { mode: 'auxiliary' })` (#1984): rede, 500 e 403 que
 *   não é de sessão ficam no painel, sem /error e sem `resetApiState`. No "Tentar de novo" o
 *   `error` antigo continua no RTK Query 1.9: `isFetching` vence, e a tela volta a carregar.
 * - Erro em cache ao montar: é de uma requisição anterior à montagem (a linha estava fechada). O
 *   RTK Query refaz a consulta na assinatura nova (a entrada não tem `fulfilledTimeStamp`); até a
 *   resposta, o painel mostra o carregando, e o erro antigo não aparece nem vai ao Sentry. Ele
 *   continua no estado do RTK durante a consulta nova, então a comparação é pela identidade do
 *   erro capturado na montagem (o `startedTimeStamp` já é o da consulta nova nesse intervalo).
 * - Sequência de falhas entre aberturas: se um painel já mostrou o erro que estava em cache, as
 *   falhas seguintes do mesmo estudo não geram evento novo no Sentry até dar certo, como no
 *   "Tentar de novo". Um erro que chegou com a linha fechada (ninguém viu) começa a sequência.
 * - 404: a invalidação da lista e o aviso no Sentry são da camada da API (`onQueryStarted` de
 *   `getMyStudyScenarios`), uma vez por requisição; aqui só a tela.
 * - Rótulo do membro: o `painel-options` do Flux, a mesma entrada de cache do
 *   `useControlPanelOptions` do formulário Novo Estudo. Sem o `useControlPanelOptions` em si: ele
 *   trata erro com o `useHandleRequestErrors`, que leva a /error e zera o cache. A falha do
 *   `painel-options` vai ao Sentry pela casca, uma vez com qualquer número de painéis abertos, e o
 *   membro fica com o id.
 *
 * @param studyId - Id do estudo da linha aberta.
 * @returns O estado do painel (ver {@link MyStudyScenariosState}).
 */
export default function useMyStudyScenarios(studyId: number): MyStudyScenariosState {
  const {
    currentData, error, isFetching, refetch,
  } = useGetMyStudyScenariosQuery(studyId);
  const { data: controlPanelOptions } = useControlPanelOptionsQuery('flux');

  const [errorAtMount] = useState(error);
  const [continuesSequence] = useState(() => isObject(error) && errorsShownInPanels.has(error));
  const freshError = error === errorAtMount ? undefined : error;

  useEffect(() => {
    if (isObject(freshError)) errorsShownInPanels.add(freshError);
  }, [freshError]);

  const notFound = isStudyNotFound(freshError);
  useStudiesErrorState(notFound || continuesSequence ? undefined : freshError, { mode: 'auxiliary' });

  const retry = useCallback(() => {
    refetch();
  }, [refetch]);

  const memberLabelOf = useCallback((block: MyStudyBlock) => (
    resolveMemberLabel(controlPanelOptions, block.model, block.member)
  ), [controlPanelOptions]);

  let view: MyStudyScenariosView = 'loading';
  if (currentData !== undefined) view = 'ready';
  else if (freshError !== undefined && !isFetching) view = notFound ? 'not-found' : 'error';

  return {
    view,
    scenarios: currentData ?? NO_SCENARIOS,
    retry,
    memberLabelOf,
  };
}
